import { appsPath, useScope } from "../lib/scope";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircleIcon, PlusIcon } from "@primer/octicons-react";
import { useState } from "react";
import { api } from "../api/client";
import { useWhoami } from "../api/hooks";
import { CopyField, Empty, ErrorMessage, Loading } from "../components/common";
import { Subhead } from "../components/SettingsLayout";
import { Badge } from "../components/ui/badge";
import { Alert, AlertTitle } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { CheckboxField } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Field, FieldStack } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

type AppRow = {
  name: string;
  owner: string;
  webhook_url: string;
  permissions: string[];
  disabled: boolean;
};

type Delivery = { id: number; status: string; attempts: number; last_error: string; event_id: number };

const PERMISSIONS = [
  { id: "read", desc: "Read changes and files." },
  { id: "checks", desc: "Post check results on changes." },
  { id: "comment", desc: "Comment on changes." },
  { id: "write", desc: "Open and land changes." },
  { id: "review", desc: "Approve or reject changes." },
];
const csv = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);
const NAME_RE = /^[a-z0-9][a-z0-9._-]{0,62}$/;

const tone = { delivered: "green", dead: "red", pending: "yellow", inflight: "blue" } as const;

export function AdminApps() {
  const org = useScope();
  const qc = useQueryClient();
  const apps = useQuery({ queryKey: ["admin", "apps", org], queryFn: () => api.get<{ apps: AppRow[] }>(appsPath(org)) });
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [installing, setInstalling] = useState("");
  const deliveries = useQuery({
    queryKey: ["admin", "deliveries", org, name],
    enabled: name !== "",
    queryFn: () => api.get<{ deliveries: Delivery[] }>(appsPath(org, `/${name}/deliveries`)),
  });
  const redeliver = useMutation({
    mutationFn: (id: number) => api.send("POST", appsPath(org, `/${name}/deliveries/${id}/redeliver`), {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "deliveries", name] }),
  });
  return (
    <>
      <Subhead
        title="Webhooks"
        actions={
          <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
            <PlusIcon /> New webhook
          </Button>
        }
      >
        Send change events to a URL, such as a scanner that reports a check back. Each webhook gets its own token and signing secret.
      </Subhead>
      {apps.isLoading ? <Loading /> : null}
      {apps.error ? <ErrorMessage error={apps.error} /> : null}
      {apps.data && apps.data.apps.length === 0 ? (
        <Empty>
          No webhooks yet. Use <b>New webhook</b>, or run <code>bee admin app create &lt;name&gt; --webhook &lt;url&gt;</code>.
        </Empty>
      ) : null}
      {apps.data && apps.data.apps.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Can</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {apps.data.apps.map((a) => (
              <TableRow key={a.name}>
                <TableCell>
                  <Button variant="ghost" onClick={() => setName(a.name)} title="Show webhook deliveries">
                    {a.name}
                  </Button>
                  {a.disabled ? <Badge tone="red">disabled</Badge> : null}
                </TableCell>
                <TableCell className="break-all font-mono text-xs">{a.webhook_url || "—"}</TableCell>
                <TableCell>{(a.permissions ?? []).join(", ")}</TableCell>
                <TableCell>
                  <Button size="sm" onClick={() => setInstalling(a.name)}>
                    Install
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
      {name ? (
        <section className="mt-6">
          <Subhead title={`Webhook deliveries · ${name}`}>
            Failed deliveries are retried automatically. After 8 failures the webhook is disabled; fix its URL, then send again.
          </Subhead>
          {deliveries.isLoading ? <Loading /> : null}
          {deliveries.error ? <ErrorMessage error={deliveries.error} /> : null}
          {redeliver.error ? <ErrorMessage error={redeliver.error} /> : null}
          {deliveries.data && deliveries.data.deliveries.length === 0 ? <Empty>No deliveries yet.</Empty> : null}
          {deliveries.data && deliveries.data.deliveries.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Last error</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.data.deliveries.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.event_id}</TableCell>
                    <TableCell>
                      <Badge tone={tone[d.status as keyof typeof tone] ?? "default"}>{d.status}</Badge>
                    </TableCell>
                    <TableCell>{d.attempts}</TableCell>
                    <TableCell>{d.last_error || "—"}</TableCell>
                    <TableCell>
                      {d.status === "delivered" || d.status === "dead" ? (
                        <Button disabled={redeliver.isPending} onClick={() => redeliver.mutate(d.id)}>
                          Send again
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </section>
      ) : null}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>{creating && <NewApp onDone={() => setCreating(false)} />}</DialogContent>
      </Dialog>
      <Dialog open={installing !== ""} onOpenChange={(o) => !o && setInstalling("")}>
        <DialogContent>{installing && <InstallApp name={installing} onDone={() => setInstalling("")} />}</DialogContent>
      </Dialog>
    </>
  );
}

function NewApp({ onDone }: { onDone: () => void }) {
  const org = useScope();
  const qc = useQueryClient();
  const me = useWhoami();
  const [f, setF] = useState({ name: "", webhook: "", perms: ["read"] });
  const set = (k: "name" | "webhook") => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const create = useMutation({
    mutationFn: () =>
      api.send<{ app: AppRow; token: string; webhook_secret: string }>("POST", appsPath(org), {
        name: f.name,
        owner: me.data?.sub,
        webhook_url: f.webhook,
        permissions: f.perms,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "apps"] }),
  });
  const nameBad = f.name !== "" && !NAME_RE.test(f.name);
  const toggle = (id: string, on: boolean) => setF({ ...f, perms: on ? [...f.perms, id] : f.perms.filter((p) => p !== id) });

  if (create.data)
    return (
      <div className="px-4 py-4">
        <Alert variant="success" className="mt-0">
          <AlertTitle>
            <CheckCircleIcon /> {create.data.app.name} created. Copy both now: they are not shown again.
          </AlertTitle>
          <div className="mt-3 mb-1 text-xs text-muted-foreground">Token</div>
          <CopyField value={create.data.token} title="Copy token" className="bg-background" />
          <div className="mt-3 mb-1 text-xs text-muted-foreground">Webhook secret</div>
          <CopyField value={create.data.webhook_secret} title="Copy webhook secret" className="bg-background" />
          <p className="mt-3 text-xs text-muted-foreground">Give both to the receiver, then install it on the repositories it should see.</p>
          <Button className="mt-3" onClick={onDone}>
            Done
          </Button>
        </Alert>
      </div>
    );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <div className="border-b border-border-muted px-4 py-3">
        <DialogTitle className="text-sm font-semibold">New webhook</DialogTitle>
      </div>
      <div className="px-4 py-4">
        <FieldStack>
          <Field label="Name" required invalid={nameBad} hint={nameBad ? "Lowercase letters, digits, '.', '_' or '-'; up to 63 characters." : "Used in the audit log."}>
            <Input autoFocus value={f.name} onChange={set("name")} placeholder="scanner" aria-invalid={nameBad} required />
          </Field>
          <Field label="URL" required hint="https URL that receives events.">
            <Input mono type="url" value={f.webhook} onChange={set("webhook")} placeholder="https://example.com/hooks/bee" required />
          </Field>
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">Permissions (default: read)</summary>
            <fieldset className="mt-3">
              {PERMISSIONS.map((p) => (
                <CheckboxField key={p.id} title={p.id} description={p.desc} checked={f.perms.includes(p.id)} onCheckedChange={(c) => toggle(p.id, c === true)} />
              ))}
            </fieldset>
          </details>
        </FieldStack>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border-muted px-4 py-3">
        {create.error ? <ErrorMessage error={create.error} /> : null}
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" disabled={create.isPending || !f.name || !f.webhook || nameBad || !f.perms.length}>
          {create.isPending ? "Creating…" : "Create webhook"}
        </Button>
      </div>
    </form>
  );
}

function InstallApp({ name, onDone }: { name: string; onDone: () => void }) {
  const org = useScope();
  const [repos, setRepos] = useState("");
  const [paths, setPaths] = useState("");
  const install = useMutation({
    mutationFn: () => api.send("POST", appsPath(org, `/${name}/installations`), { repos: csv(repos), ...(paths ? { paths: csv(paths) } : {}) }),
    onSuccess: onDone,
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        install.mutate();
      }}
    >
      <div className="border-b border-border-muted px-4 py-3">
        <DialogTitle className="text-sm font-semibold">Install {name}</DialogTitle>
      </div>
      <div className="px-4 py-4">
        <FieldStack>
          <Field label="Repositories" required hint="Comma-separated names, or * for every repository. The app gets webhooks for these.">
            <Input autoFocus value={repos} onChange={(e) => setRepos(e.target.value)} placeholder="bee, docs" required />
          </Field>
          <Field label="Paths" hint="Comma-separated globs the app may touch. Empty means every path.">
            <Input mono value={paths} onChange={(e) => setPaths(e.target.value)} placeholder="src/**" />
          </Field>
        </FieldStack>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border-muted px-4 py-3">
        {install.error ? <ErrorMessage error={install.error} /> : null}
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" disabled={install.isPending || !csv(repos).length}>
          {install.isPending ? "Installing…" : "Install"}
        </Button>
      </div>
    </form>
  );
}
